import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  answerApi,
  chatApi,
  canvasApi,
  debugApi,
  llmApi,
  memoryApi,
  skillsApi,
  imageUrl,
  publishApi,
  PUBLICATIONS_CHANGED_EVENT,
} from "../api/client";
import { loadAutoMemoryExtraction } from "../lib/memoryPrefs";
import { loadSkipToolConfirmations } from "../lib/toolPrefs";
import { loadWebSearch, saveWebSearch } from "../lib/webSearchPrefs";
import { loadThinkingEffort, saveThinkingEffort } from "../lib/thinkingPrefs";
import * as debugCapture from "../lib/debugCapture";
import type {
  AnswerSource,
  AnswerSSEEvent,
  Chat,
  CitationReport,
  CanvasMode,
  Message,
  Skill,
  SSEEvent,
  ModelsByProvider,
  SearchProviderName,
  SearchProviderWarning,
  SitePublication,
} from "../types";

import Markdown from "../components/Markdown";
import PublicationCard from "../components/PublicationCard";
import ToolCallPanel from "../components/ToolCallPanel";
import ConfirmCard from "../components/ConfirmCard";
import SourceRail from "../components/SourceRail";
import PlacesPanel from "../components/PlacesPanel";
import StagePill from "../components/StagePill";
import RelatedQuestions from "../components/RelatedQuestions";
import UnverifiedBadge from "../components/UnverifiedBadge";
import SlashCommandAutocomplete, {
  type PluginCommand,
} from "../components/SlashCommandAutocomplete";
import CanvasPane from "../components/CanvasPane";
import { useToast } from "../lib/toast";
import { useShell, useViewport } from "../lib/shell";
import {
  type ModelBackend,
  type ModelSelection,
  clearLastModelSelection,
  decodeModelSelection,
  encodeModelSelection,
  saveLastModelSelection,
} from "../lib/chatModel";
import "./ChatView.css";

type Stage = "understanding" | "searching" | "reading";

export const MAX_VIDEO_SIZE_BYTES = 35 * 1024 * 1024;
// Keep this list explicit so the native picker is useful on desktop and
// mobile, while drag-and-drop remains permissive for files with an unknown
// MIME type. The model/provider remains the authority on support.
export const ATTACHMENT_ACCEPT = [
  "image/*",
  "video/*",
  "audio/*",
  "text/plain",
  "text/markdown",
  "text/html",
  "text/css",
  "text/xml",
  "application/json",
  "application/xml",
  "application/pdf",
  "application/rtf",
  "application/zip",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.oasis.opendocument.text",
  "application/vnd.oasis.opendocument.spreadsheet",
  "application/vnd.oasis.opendocument.presentation",
  ".md", ".markdown", ".mdx", ".txt", ".csv", ".tsv", ".json", ".xml", ".html", ".htm",
  ".css", ".scss", ".sass", ".less", ".js", ".jsx", ".mjs", ".cjs", ".ts", ".tsx",
  ".py", ".java", ".c", ".h", ".cpp", ".hpp", ".cs", ".go", ".rs", ".rb", ".php",
  ".swift", ".kt", ".kts", ".sh", ".bash", ".sql", ".yaml", ".yml", ".toml", ".ini",
  ".doc", ".docx", ".xls", ".xlsx", ".ppt", ".pptx", ".odt", ".ods", ".odp", ".pdf",
].join(",");

/**
 * Natural-language requests to build a web artifact should get the staged
 * grounding flow. Keep this deliberately narrow: mentioning a site while
 * asking a normal question must not silently switch the tool policy.
 */
export function detectCanvasBuildIntent(content: string): boolean {
  const text = content.toLocaleLowerCase();
  const canvasMention = /\bcanvas\b/.test(text);
  const canvasAction = /\b(use|build|create|make|design|develop|code|generate|implement|scaffold|update|edit|change|modify|apply)\b/.test(text);
  // P1 regression fix: a bare "site" used to count as a web-artifact
  // mention on its own — but "the site" is completely ordinary phrasing
  // when a user just wants tool-augmented commentary on a fetched page
  // ("summarize the site", "what's on this site?"), not a Canvas build.
  // Require an explicit website/webapp mention instead, matching this
  // function's own intent: mentioning a site while asking a normal
  // question must not silently switch the tool policy.
  const webArtifact = /\b(website|web\s+site|web\s+app|webapp|webpage|web\s+page|landing\s+page)\b/.test(text);
  const buildAction = /\b(build|create|make|design|develop|code|generate|implement|scaffold)\b/.test(text);
  return (canvasMention && canvasAction) || (webArtifact && buildAction);
}

interface PendingTool {
  id: string;
  name: string;
  arguments: Record<string, unknown>;
  result?: string;
}

interface PendingConfirmation {
  id: string;
  name: string;
  arguments: Record<string, unknown>;
  source: string;
}

export default function ChatView() {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [chat, setChat] = useState<Chat | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [pendingTools, setPendingTools] = useState<PendingTool[]>([]);
  // Task 9.3 — the suspended turn's Confirm-tier tool call, if any. Set from
  // `tool_confirm_required` / `done.pending_confirmation`; cleared once a
  // decision is posted.
  const [pendingConfirmation, setPendingConfirmation] =
    useState<PendingConfirmation | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  // F8-2/F8-3 fix: separate load errors from this-turn streaming errors, and
  // separate "streaming failed" from "streaming was cleanly truncated by
  // Stop" — collapsing all three into one `error` string used to erase a
  // genuine error the instant anything else happened (e.g. a stray
  // setError(null) on the next successful load).
  const [loadError, setLoadError] = useState<string | null>(null);
  const [streamError, setStreamError] = useState<string | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [catalog, setCatalog] = useState<ModelsByProvider>({});
  const [defaultSelection, setDefaultSelection] = useState<ModelSelection | null>(null);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [activeSkills, setActiveSkills] = useState<string[]>([]);
  const [useMemory, setUseMemory] = useState(true);
  const [useTools, setUseTools] = useState(true);
  // search-overhaul spec — Web Search is decoupled from Tools: it grounds
  // ordinary chat replies with search results, independent of whether
  // tool-calling is on. Research Answer (answerMode below) is the separate
  // full sourced-answer pipeline; this toggle has no effect on it.
  const [useWebSearch, setUseWebSearch] = useState(loadWebSearch);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  // Task 9.9 — "answer mode": POST /api/answer's search+cite pipeline
  // instead of the plain tool-loop chat endpoint. This is the product's
  // core loop (§0.1) — the components below (StagePill/SourceRail/
  // RelatedQuestions/UnverifiedBadge) existed and were unit-tested since
  // Tasks 5.7/5.11 but nothing ever called the endpoint or rendered them
  // together in a real view until this task.
  const [answerMode, setAnswerMode] = useState(false);
  const [canvasOpen, setCanvasOpen] = useState(false);
  // One-shot explicit intent: opening Canvas means the next ordinary chat
  // turn must use the first-party canvas tools, even if MCP/plugin tools are
  // normally disabled.
  const [canvasRequested, setCanvasRequested] = useState(false);
  const [canvasRevisionTick, setCanvasRevisionTick] = useState(0);
  const [activeCanvasWorkspaceId, setActiveCanvasWorkspaceId] = useState<string | null>(null);
  // Sites published from this chat's canvases. Kept apart from `messages`
  // on purpose: a publication is a record in the conversation, never a turn
  // the model sees.
  const [publications, setPublications] = useState<SitePublication[]>([]);
  const [hereNowConnected, setHereNowConnected] = useState(false);
  const [turnReceipt, setTurnReceipt] = useState<{ status: "complete" | "stopped" | "failed"; summary: string } | null>(null);
  const [answerStage, setAnswerStage] = useState<Stage | null>(null);
  const [answerStageProviders, setAnswerStageProviders] = useState<SearchProviderName[] | undefined>(undefined);
  const [answerStageExecution, setAnswerStageExecution] = useState<"serial" | "parallel" | undefined>(undefined);
  const [chatSearchStage, setChatSearchStage] = useState<{ providers: SearchProviderName[]; execution: "serial" | "parallel" } | null>(null);
  const [liveSources, setLiveSources] = useState<AnswerSource[] | null>(null);
  const [liveProviderErrors, setLiveProviderErrors] = useState<SearchProviderWarning[] | undefined>(undefined);
  const [liveReport, setLiveReport] = useState<CitationReport | null>(null);
  // Not cleared on completion (unlike streamingText/pendingTools) — related
  // questions aren't persisted on the Message row, so this is the only
  // place they can still be shown once the turn's message has reloaded.
  // Cleared at the start of the next send/sendAnswer instead.
  const [liveRelated, setLiveRelated] = useState<string[] | null>(null);
  const [liveActiveN, setLiveActiveN] = useState<number | null>(null);
  const [skillsOpen, setSkillsOpen] = useState(false);
  // Composer Thinking dropdown. `null` = provider default (field omitted).
  const [thinkingOpen, setThinkingOpen] = useState(false);
  const [thinkingEffort, setThinkingEffort] = useState<string | null>(null);
  const [modelPickerOpen, setModelPickerOpen] = useState(false);
  // Compact layout: the composer's option toggles collapse behind a "+"
  // (ChatGPT/Claude's pattern) and the header's chat actions behind a "⋯",
  // so the composer is a single thumb-height bar instead of a third of the
  // screen. Nothing is removed — both open in place.
  const { isCompact, isPhone } = useViewport();
  const { setPageTitle } = useShell();
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [chatMenuOpen, setChatMenuOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const composerInputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const skillsPopoverRef = useRef<HTMLDivElement>(null);
  const thinkingPopoverRef = useRef<HTMLDivElement>(null);
  const modelPickerRef = useRef<HTMLDivElement>(null);
  // F8-1 fix: a real AbortController, not a polled boolean — Stop calls
  // .abort(), which both cancels the underlying fetch and (via
  // lib/sseClient.ts) explicitly cancels the stream reader.
  const abortControllerRef = useRef<AbortController | null>(null);
  // F8-6 fix: the chat id this in-flight stream belongs to, so a stream
  // started on chat A whose events keep arriving after the user navigates
  // to chat B never writes into chat B's state.
  const streamChatIdRef = useRef<string | null>(null);
  const pendingModelSaveRef = useRef<Promise<unknown> | null>(null);
  const currentChatIdRef = useRef<string | undefined>(chatId);
  currentChatIdRef.current = chatId;

  // The compact top bar shows the current chat's title (the view's own
  // <h2> is hidden on a phone — two titles in 52px of chrome is noise).
  useEffect(() => {
    setPageTitle(chat?.title || "Chat");
  }, [chat?.title, setPageTitle]);

  // Auto-grow composer: one line at rest, growing with the text up to a
  // cap, then scrolling. A fixed 70px-tall textarea is what made the
  // composer eat a third of a phone screen while empty.
  useEffect(() => {
    const el = composerInputRef.current;
    if (!el) return;
    el.style.height = "auto";
    const max = isPhone ? 150 : 220;
    el.style.height = `${Math.min(el.scrollHeight, max)}px`;
    // Only scroll once the text actually exceeds the cap — otherwise the
    // browser paints a scrollbar track over a one-line field.
    el.style.overflowY = el.scrollHeight > max ? "auto" : "hidden";
  }, [input, isPhone]);

  // Load models + skills once
  useEffect(() => {
    llmApi.models(false).then(setCatalog).catch(() => {});
    skillsApi.list().then(setSkills).catch(() => {});
    // Resolved default for the "chat" feature — a per-feature override
    // beats the global default, mirroring model_resolver.py's precedence
    // (approximated here just for the dropdown label, not authoritative).
    if (typeof llmApi.settings === "function") {
      llmApi.settings().then((s) => {
        const chatOverride = s.overrides?.chat;
        const backend: ModelBackend = (chatOverride?.backend ?? s.backend) === "ollama"
          ? "ollama"
          : "cloudflare";
        const model = chatOverride?.model ?? s.model;
        const provider = chatOverride?.provider ?? (
          backend === "ollama"
            ? "ollama"
            : Object.keys(catalog).find((p) => catalog[p]?.models.some((m) => m.id === model)) ?? ""
        );
        setDefaultSelection({ backend, provider, model });
      }).catch(() => {});
    }
  }, []);

  const loadChat = useCallback(async () => {
    if (!chatId) {
      setChat(null);
      setMessages([]);
      return;
    }
    try {
      const c = await chatApi.get(chatId);
      setChat(c);
      const msgs = await chatApi.messages(chatId);
      setMessages(msgs);
      const canvases = await canvasApi.list(chatId);
      setActiveCanvasWorkspaceId(canvases[canvases.length - 1]?.id ?? null);
      setLoadError(null);
    } catch (e) {
      setLoadError((e as Error).message);
    }
  }, [chatId]);

  useEffect(() => {
    loadChat();
    setLiveRelated(null);
  }, [loadChat]);

  // Best-effort: a publications failure must never block the chat itself.
  useEffect(() => {
    if (!chatId) {
      setPublications([]);
      return;
    }
    let cancelled = false;
    const load = () =>
      publishApi.list({ chat_id: chatId })
        .then((rows) => { if (!cancelled) setPublications(rows); })
        .catch(() => { if (!cancelled) setPublications([]); });
    void load();
    publishApi.connection().then((c) => { if (!cancelled) setHereNowConnected(c.connected); }).catch(() => {});
    window.addEventListener(PUBLICATIONS_CHANGED_EVENT, load);
    return () => {
      cancelled = true;
      window.removeEventListener(PUBLICATIONS_CHANGED_EVENT, load);
    };
  }, [chatId]);

  // Attribution for the capture buffers, which outlive any one chat. Keyed on
  // the route param rather than the loaded `chat` so requests made *during*
  // the load are attributed too, and cleared on unmount so nothing captured
  // outside a chat inherits the last one's id.
  useEffect(() => {
    debugCapture.setCurrentChat(chatId ?? null);
    return () => debugCapture.setCurrentChat(null);
  }, [chatId]);

  // F8-6 fix: abort any in-flight stream when navigating away from this
  // chat (id changes, or the view unmounts) — previously nothing aborted
  // the fetch on navigation, so a still-arriving token from the old chat
  // could land in the new chat's state.
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, [chatId]);

  // Close the skills popover when clicking outside it.
  useEffect(() => {
    if (!skillsOpen) return;
    const onDown = (e: MouseEvent) => {
      if (
        skillsPopoverRef.current &&
        !skillsPopoverRef.current.contains(e.target as Node)
      ) {
        setSkillsOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [skillsOpen]);

  useEffect(() => {
    if (!thinkingOpen) return;
    const onDown = (e: MouseEvent) => {
      if (
        thinkingPopoverRef.current &&
        !thinkingPopoverRef.current.contains(e.target as Node)
      ) {
        setThinkingOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [thinkingOpen]);

  useEffect(() => {
    if (!modelPickerOpen) return;
    const onDown = (e: MouseEvent) => {
      if (modelPickerRef.current && !modelPickerRef.current.contains(e.target as Node)) {
        setModelPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [modelPickerOpen]);

  // Auto scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streamingText, pendingTools]);

  // Task 9.3 — shared consumption + persistence logic for both a fresh
  // `sendMessage` stream and a resumed `decision` stream: both emit the same
  // event shapes (token/tool_call/tool_result/tool_confirm_required/done/
  // error), so a Confirm-tier turn can suspend, resume, and suspend again
  // (a second tool call in the same batch) through the same code path.
  const runTurn = useCallback(
    async (gen: AsyncGenerator<SSEEvent>, thisChatId: string, controller: AbortController) => {
      const isStale = () => streamChatIdRef.current !== currentChatIdRef.current;
      let accumulated = "";
      let wasAborted = false;
      let turnFailed = false;
      let suspended: PendingConfirmation | null = null;
      try {
        for await (const evt of gen) {
          if (isStale()) break;
          if (evt.type === "token") {
            accumulated += evt.content;
            setStreamingText(accumulated);
            setChatSearchStage(null);
          } else if (evt.type === "stage") {
            setChatSearchStage({ providers: evt.providers, execution: evt.execution });
          } else if (evt.type === "tool_call") {
            setTurnReceipt(null);
            setChatSearchStage(null);
            setPendingTools((p) => [
              ...p,
              { id: evt.id, name: evt.name, arguments: evt.arguments },
            ]);
          } else if (evt.type === "tool_result") {
            setPendingTools((p) =>
              p.map((t) => (t.id === evt.id ? { ...t, result: evt.result } : t))
            );
          } else if (evt.type === "canvas_revision") {
            setActiveCanvasWorkspaceId(evt.workspace_id);
            setCanvasOpen(true);
            setCanvasRevisionTick((tick) => tick + 1);
          } else if (evt.type === "tool_confirm_required") {
            suspended = {
              id: evt.id,
              name: evt.name,
              arguments: evt.arguments,
              source: evt.source,
            };
            setPendingConfirmation(suspended);
          } else if (evt.type === "done") {
            if (evt.message && !accumulated) {
              accumulated = evt.message;
              setStreamingText(accumulated);
            }
            if (evt.pending_confirmation && suspended) {
              // Already captured from tool_confirm_required above; nothing
              // further to do — the turn stays suspended.
            }
            break;
          } else if (evt.type === "error") {
            setStreamError(evt.content);
            debugCapture.recordUIError("chat-stream", evt.content);
            turnFailed = true;
            break;
          }
        }
      } catch (e) {
        if (controller.signal.aborted || (e as Error)?.name === "AbortError") {
          wasAborted = true;
        } else if (!isStale()) {
          setStreamError((e as Error).message);
          debugCapture.recordUIError("chat-stream", (e as Error).message, (e as Error).stack);
          turnFailed = true;
        }
      }

      if (isStale()) return false;

      if (controller.signal.aborted) wasAborted = true;

      if (wasAborted) {
        setTruncated(true);
        if (accumulated) {
          setMessages((m) => [
            ...m,
            {
              id: `tmp-truncated-${Date.now()}`,
              chat_id: thisChatId,
              role: "assistant",
              content: accumulated,
              created_at: new Date().toISOString(),
              truncated: true,
            },
          ]);
        }
        setStreaming(false);
        setStreamingText("");
        setPendingTools([]);
        setTurnReceipt({ status: "stopped", summary: "Stopped before the turn finished." });
        return false;
      }

      if (suspended) {
        // Turn is suspended pending a decision — keep pendingTools/text
        // visible, stop the "streaming" spinner, and leave streaming=false
        // so the composer re-enables while the ConfirmCard is answered.
        setStreaming(false);
        return false;
      }

      await loadChat();
      if (isStale()) return;
      setStreaming(false);
      setStreamingText("");
      setPendingTools([]);
      setTurnReceipt({
        status: turnFailed ? "failed" : "complete",
        summary: turnFailed ? "The turn ended with an error." : "Turn complete.",
      });
      return !turnFailed;
    },
    [loadChat]
  );

  const send = async () => {
    if (!chat || !input.trim() || streaming) return;
    if (pendingModelSaveRef.current) {
      await pendingModelSaveRef.current.catch(() => undefined);
    }
    const content = input.trim();
    const canvasEditIntent = /\b(update|edit|change|modify|add|remove|replace|refresh|revise|apply)\b/i.test(content);
    const shouldUseCanvas = canvasRequested || Boolean(activeCanvasWorkspaceId && canvasOpen && canvasEditIntent);
    const shouldResearchThenCanvas = !shouldUseCanvas && detectCanvasBuildIntent(content);
    const canvasMode: CanvasMode = shouldUseCanvas
      ? "required"
      : shouldResearchThenCanvas
        ? "research_then_canvas"
        : "auto";
    const thisChatId = chat.id;
    streamChatIdRef.current = thisChatId;
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setInput("");
    setStreamError(null);
    setTurnReceipt(null);
    setTruncated(false);
    setStreaming(true);
    setStreamingText("");
    setPendingTools([]);
    setPendingConfirmation(null);
    setLiveRelated(null);
    setChatSearchStage(null);

    // Optimistically add user message (with local attachment previews).
    // Blob URLs do not retain enough metadata to distinguish a pending PDF
    // from a pending image in the message renderer. Non-image files are
    // rendered from their persisted data URL once the turn reloads.
    const userImages = pendingFiles
      .filter((f) => f.type.startsWith("image/"))
      .map((f) => URL.createObjectURL(f));
    const optimistic: Message = {
      id: `tmp-${Date.now()}`,
      chat_id: chat.id,
      role: "user",
      content,
      images: userImages.length ? userImages : null,
      created_at: new Date().toISOString(),
    };
    setMessages((m) => [...m, optimistic]);

    // If images present, upload to vision endpoint first to get paths
    let imagePaths: string[] = [];
    if (pendingFiles.length > 0) {
      try {
        // Use vision/analyze is for analysis; for chat with images we need
        // files available server-side. We send via the chat body's images
        // field as data URLs which backend can read. Simpler: read as base64.
        const base64 = await Promise.all(
          pendingFiles.map((f) => fileToBase64(f))
        );
        imagePaths = base64;
      } catch (e) {
        setStreamError("Failed to read images: " + (e as Error).message);
      }
    }
    setPendingFiles([]);

    const gen = chatApi.sendMessage(
      chat.id,
      {
        content,
        images: imagePaths.length ? imagePaths : undefined,
        active_skills: activeSkills.length ? activeSkills : undefined,
        use_memory: useMemory,
        use_tools: shouldUseCanvas || shouldResearchThenCanvas || useTools,
        web_search: useWebSearch,
        canvas_mode: canvasMode,
        active_workspace_id: activeCanvasWorkspaceId ?? undefined,
        auto_extract: loadAutoMemoryExtraction(),
        reasoning_effort: thinkingEffort ?? undefined,
        skip_tool_confirmations: loadSkipToolConfirmations(),
      },
      controller.signal
    );
    const turnCompleted = await runTurn(gen, thisChatId, controller);
    setCanvasRequested(false);
    if (streamChatIdRef.current !== currentChatIdRef.current) return;

    // Auto-title if it's a new chat
    if (turnCompleted && chat.title === "New Chat") {
      chatApi.autoTitle(chat.id).then(() => loadChat()).catch(() => {});
    }
  };

  // Task 9.9 — answer-mode send: POST /api/answer and consume the
  // documented event sequence (stage -> sources [before any token] ->
  // token -> citation_report -> related -> done). Kept as its own function
  // rather than folded into runTurn() — the event union and what each
  // event does to the UI (stage pill, source rail, related chips) are
  // different enough from the tool-loop's that sharing one loop would
  // mean type-narrowing games at every branch for no real reuse.
  const sendAnswer = async (overrideContent?: string) => {
    if (!chat || streaming) return;
    const content = (overrideContent ?? input).trim();
    if (!content) return;
    if (pendingModelSaveRef.current) {
      await pendingModelSaveRef.current.catch(() => undefined);
    }
    const thisChatId = chat.id;
    streamChatIdRef.current = thisChatId;
    const controller = new AbortController();
    abortControllerRef.current = controller;

    if (!overrideContent) setInput("");
    setStreamError(null);
    setTruncated(false);
    setStreaming(true);
    setStreamingText("");
    setPendingTools([]);
    setPendingConfirmation(null);
    setAnswerStage(null);
    setAnswerStageProviders(undefined);
    setAnswerStageExecution(undefined);
    setLiveSources(null);
    setLiveProviderErrors(undefined);
    setLiveReport(null);
    setLiveRelated(null);
    setLiveActiveN(null);

    const optimistic: Message = {
      id: `tmp-${Date.now()}`,
      chat_id: chat.id,
      role: "user",
      content,
      created_at: new Date().toISOString(),
    };
    setMessages((m) => [...m, optimistic]);

    const isStale = () => streamChatIdRef.current !== currentChatIdRef.current;
    let accumulated = "";
    let wasAborted = false;
    try {
      const gen = answerApi.stream(chat.id, content, controller.signal);
      for await (const evt of gen as AsyncGenerator<AnswerSSEEvent>) {
        if (isStale()) break;
        if (evt.type === "stage") {
          setAnswerStage(evt.stage);
          setAnswerStageProviders(evt.providers ?? undefined);
          setAnswerStageExecution(evt.execution ?? undefined);
        } else if (evt.type === "sources") {
          // Must render before any token (§0.6) — the stage pill's job is
          // done the instant sources land, even if tokens haven't started.
          setLiveSources(evt.sources);
          setLiveProviderErrors(evt.provider_errors);
          setAnswerStage(null);
          setAnswerStageProviders(undefined);
          setAnswerStageExecution(undefined);
        } else if (evt.type === "token") {
          accumulated += evt.content;
          setStreamingText(accumulated);
        } else if (evt.type === "citation_report") {
          setLiveReport(evt.report);
        } else if (evt.type === "related") {
          setLiveRelated(evt.questions);
        } else if (evt.type === "error") {
          setStreamError(evt.message);
          debugCapture.recordUIError("answer-stream", evt.message);
          break;
        } else if (evt.type === "done") {
          break;
        }
      }
    } catch (e) {
      if (controller.signal.aborted || (e as Error)?.name === "AbortError") {
        wasAborted = true;
      } else if (!isStale()) {
        setStreamError((e as Error).message);
        debugCapture.recordUIError("answer-stream", (e as Error).message, (e as Error).stack);
      }
    }

    if (isStale()) return;
    if (controller.signal.aborted) wasAborted = true;

    if (wasAborted) {
      setTruncated(true);
      if (accumulated) {
        setMessages((m) => [
          ...m,
          {
            id: `tmp-truncated-${Date.now()}`,
            chat_id: thisChatId,
            role: "assistant",
            content: accumulated,
            created_at: new Date().toISOString(),
            truncated: true,
          },
        ]);
      }
      setStreaming(false);
      setStreamingText("");
      return;
    }

    await loadChat();
    if (isStale()) return;
    setStreaming(false);
    setStreamingText("");
    setAnswerStage(null);
    setAnswerStageProviders(undefined);
    setAnswerStageExecution(undefined);
    setLiveSources(null);
    setLiveProviderErrors(undefined);
    setLiveReport(null);
    // liveRelated intentionally kept — see its declaration above.

    if (chat.title === "New Chat") {
      chatApi.autoTitle(chat.id).then(() => loadChat()).catch(() => {});
    }
  };

  // Task 9.3 — Approve/Deny on the ConfirmCard: POST the decision and
  // consume the resumed stream through the same runTurn path (which may
  // suspend again on a second Confirm-tier call in the same batch).
  // `approveAll` also skips the remaining confirmations for this turn; the
  // Settings toggle is re-read on every send, so it never outlives the turn.
  const respondToConfirmation = async (approve: boolean, approveAll = false) => {
    if (!chat || !pendingConfirmation) return;
    const tcId = pendingConfirmation.id;
    const thisChatId = chat.id;
    setConfirmBusy(true);
    setPendingConfirmation(null);
    setStreaming(true);
    const controller = new AbortController();
    abortControllerRef.current = controller;
    streamChatIdRef.current = thisChatId;
    try {
      const gen = chatApi.decision(
        chat.id, tcId, approve, controller.signal, useMemory, loadAutoMemoryExtraction(),
        approveAll || loadSkipToolConfirmations(),
      );
      await runTurn(gen, thisChatId, controller);
    } finally {
      setConfirmBusy(false);
    }
  };

  const stop = () => {
    // F8-1 fix: actually abort the fetch + cancel the stream reader,
    // instead of setting a flag the render loop merely polled between
    // events (which let the server — and the visible token stream — keep
    // going right up to its natural end).
    abortControllerRef.current?.abort();
  };

  const onFileSelect = (files: FileList | null) => {
    if (!files) return;
    const accepted: File[] = [];
    const rejectedVideos: string[] = [];
    Array.from(files).forEach((file) => {
      if (file.type.startsWith("video/") && file.size > MAX_VIDEO_SIZE_BYTES) {
        rejectedVideos.push(file.name);
        return;
      }
      accepted.push(file);
    });
    if (rejectedVideos.length > 0) {
      toast.error(`${rejectedVideos.join(", ")} exceeds the 35 MB video limit.`);
    }
    if (accepted.length > 0) setPendingFiles((p) => [...p, ...accepted]);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    onFileSelect(e.dataTransfer.files);
  };

  const deleteChat = async () => {
    if (!chat) return;
    if (!confirm("Delete this chat?")) return;
    await chatApi.remove(chat.id);
    navigate("/");
  };

  // Phase D5 — merges the frontend's own buffers (network/SSE/console/UI
  // errors, all in-memory only) with the backend's per-chat capture export
  // (DB rows + request/LLM/tool/log events) and asks the backend to write
  // the combined bundle to a file on the server (upload_dir/debug_captures,
  // a mounted persistent volume) rather than downloading a Blob to the
  // browser. On the prod VPS the browser is the operator's laptop, so a
  // downloaded 100k-line file lands on their Desktop instead of somewhere
  // reachable from a shell on this box — saving server-side and returning
  // the path lets them just hand the path back for review. Works
  // identically for a normal chat turn and an answer-mode turn: both go
  // through the same authedFetch/parseAnswerStream teeing in
  // api/client.ts, so there's nothing answer-mode-specific to do here.
  const captureEvent = async () => {
    if (!chat) return;
    try {
      const frontend = debugCapture.snapshot(chat.id);
      const saved = await debugApi.saveChatCapture(chat.id, frontend);
      await navigator.clipboard?.writeText(saved.path).catch(() => {});
      toast.success(`Capture saved: ${saved.path} (copied to clipboard)`);
    } catch (error) {
      toast.error((error as Error).message || "Could not save capture.");
    }
  };

  const setModel = async (selection: ModelSelection | null) => {
    if (!chat) return;
    // Direct Ollama has no real gateway "provider" -- never persist the
    // "ollama" placeholder used to pick a model list as if it were an
    // actual provider name, or the backend's catalog validation (which
    // only knows real gateway vendor names) rejects the override.
    const toPersist =
      selection && selection.backend === "ollama" ? { ...selection, provider: "" } : selection;
    const save = chatApi.update(chat.id, {
      model_override: toPersist ? encodeModelSelection(toPersist) : null,
    });
    pendingModelSaveRef.current = save;
    const updated = await save;
    if (pendingModelSaveRef.current === save) pendingModelSaveRef.current = null;
    setChat(updated);
    if (toPersist) saveLastModelSelection(toPersist);
    else clearLastModelSelection();
  };

  const backendProviders = (backend: ModelBackend) => {
    // Direct Ollama has no real "provider" concept -- it's a single catalog
    // bucket ("ollama-direct"), not a gateway vendor. "ollama" here is only
    // ever a placeholder to pick a model list, never persisted as a
    // provider value (see chooseBackend/chooseProvider).
    if (backend === "ollama") return catalog["ollama-direct"] ? ["ollama"] : [];
    return Object.keys(catalog)
      .filter((provider) => provider !== "ollama-direct")
      .sort((a, b) => a.localeCompare(b));
  };

  const providerModels = (backend: ModelBackend, provider: string) => {
    const key = backend === "ollama" ? "ollama-direct" : provider;
    return [...(catalog[key]?.models ?? [])].sort((a, b) =>
      a.id.localeCompare(b.id) || (provider || key).localeCompare(provider || key)
    );
  };

  const storedSelection = decodeModelSelection(chat?.model_override ?? null) ?? (
    chat?.model_override
      ? (() => {
          const entry = Object.entries(catalog).find(([, value]) =>
            value.models.some((model) => model.id === chat.model_override)
          );
          return entry
            ? { backend: entry[0] === "ollama-direct" ? "ollama" : "cloudflare", provider: entry[0] === "ollama-direct" ? "ollama" : entry[0], model: chat.model_override }
            : null;
        })()
      : defaultSelection
  );

  const chatSelection: ModelSelection | null = storedSelection
    ? {
        ...storedSelection,
        provider: storedSelection.provider || (
          storedSelection.backend === "ollama"
            ? "ollama"
            : Object.keys(catalog).find((provider) =>
                catalog[provider]?.models.some((model) => model.id === storedSelection.model)
              ) ?? ""
        ),
      }
    : null;

  const modelLabel = chatSelection
    ? `${chatSelection.provider}/${chatSelection.model}`
    : "default model";

  // Thinking dropdown capability comes from the catalog entry of the model
  // shown in the header — reasoning control is per-model, not per-provider
  // (groq's gpt-oss models take low/medium/high while llama-3.1-8b-instant
  // HTTP 400s on the same field), so the allowlist lives server-side in
  // providers/reasoning_specs.py and rides along on GET /api/llm/models.
  const selectedCatalogModel = chatSelection
    ? catalog[chatSelection.backend === "ollama" ? "ollama-direct" : chatSelection.provider]
        ?.models.find((m) => m.id === chatSelection.model) ?? null
    : null;
  const reasoningOptions = selectedCatalogModel?.reasoning_efforts ?? [];
  const toolForcedEffort = selectedCatalogModel?.tool_forced_effort ?? null;
  const modelKey = chatSelection ? `${chatSelection.provider}/${chatSelection.model}` : "";

  // Selection is remembered per model and re-validated against that model's
  // allowed values, so switching models never carries a value the new one
  // would reject.
  useEffect(() => {
    setThinkingEffort(modelKey ? loadThinkingEffort(modelKey, reasoningOptions) : null);
    setThinkingOpen(false);
    // reasoningOptions is derived from modelKey + catalog; joining it keeps
    // the effect from re-running on every render with a fresh array.
  }, [modelKey, reasoningOptions.join(",")]);

  const chooseThinking = (value: string | null) => {
    setThinkingEffort(value);
    if (modelKey) saveThinkingEffort(modelKey, value);
    setThinkingOpen(false);
  };

  const chooseBackend = async (backend: ModelBackend) => {
    const provider = backendProviders(backend)[0] ?? "";
    const model = providerModels(backend, provider)[0]?.id ?? "";
    if (provider && model) await setModel({ backend, provider, model });
  };

  const chooseProvider = async (backend: ModelBackend, provider: string) => {
    const model = providerModels(backend, provider)[0]?.id ?? "";
    if (model) await setModel({ backend, provider, model });
  };

  const toggleSkill = (name: string) => {
    setActiveSkills((s) =>
      s.includes(name) ? s.filter((x) => x !== name) : [...s, name]
    );
  };

  // F8-4 fix: tool_result was never joined to its tool_call — a persisted
  // "tool" role message rendered as its own disconnected bubble instead of
  // inside the ToolCallPanel for the call it answers. One map, built once
  // per render from the flat message list, keyed by tool_call_id.
  const toolResultsById: Record<string, string> = {};
  for (const m of messages) {
    if (m.role === "tool" && m.tool_call_id) {
      toolResultsById[m.tool_call_id] = m.content;
    }
  }

  // Each publication renders after the last message that existed when it
  // was first published (index -1: before the first message).
  const publicationsAfter = new Map<number, SitePublication[]>();
  for (const p of [...publications].sort((a, b) => a.created_at.localeCompare(b.created_at))) {
    const at = new Date(p.created_at).getTime();
    let idx = -1;
    messages.forEach((m, i) => {
      if (m.created_at && new Date(m.created_at).getTime() <= at) idx = i;
    });
    publicationsAfter.set(idx, [...(publicationsAfter.get(idx) ?? []), p]);
  }
  const renderPublications = (idx: number) =>
    (publicationsAfter.get(idx) ?? []).map((p) => (
      <div key={`pub-${p.id}`} className="chat-publication">
        <PublicationCard publication={p} connected={hereNowConnected} />
      </div>
    ));

  return (
    <div className={`view chat-view${canvasOpen ? " canvas-open" : ""}`}>
      <header className="chat-header">
        <div className="chat-header-title">
          {!isCompact && <h2 className="view-title">{chat ? chat.title : "Chat"}</h2>}
          {chat && (
            <div className="chat-model-control" ref={modelPickerRef}>
              <button
                className="tag tag-muted chat-model-trigger"
                type="button"
                aria-label={`Current model: ${modelLabel}`}
                aria-expanded={modelPickerOpen}
                onClick={() => setModelPickerOpen((open) => !open)}
              >
                <span className="chat-model-trigger-label">{modelLabel}</span>
                <span className="chat-model-trigger-caret" aria-hidden="true">⌄</span>
              </button>
              {modelPickerOpen && (
                <div className="chat-model-popover" role="dialog" aria-label="Choose chat model">
                  <div className="chat-model-popover-heading">
                    <span>Chat model</span>
                    <button
                      className="chat-model-reset"
                      type="button"
                      aria-label="default"
                      title="default"
                      onClick={() => void setModel(null)}
                    >
                      ↺
                    </button>
                  </div>
                  <div className="chat-model-fields">
                    <label>
                      <span>Gateway</span>
                      <select
                        className="select chat-model-select"
                        aria-label="LLM Gateway"
                        value={chatSelection?.backend ?? ""}
                        onChange={(e) => void chooseBackend(e.target.value as ModelBackend)}
                      >
                        <option value="" disabled>Gateway</option>
                        <option value="ollama">ollama</option>
                        <option value="cloudflare">cloudflare</option>
                      </select>
                    </label>
                    <label>
                      <span>Provider</span>
                      <select
                        className="select chat-model-select"
                        aria-label="Provider"
                        value={chatSelection?.provider ?? ""}
                        onChange={(e) => void chooseProvider(chatSelection?.backend ?? "cloudflare", e.target.value)}
                        disabled={!chatSelection}
                      >
                        <option value="" disabled>Provider</option>
                        {backendProviders(chatSelection?.backend ?? "cloudflare").map((provider) => (
                          <option key={provider} value={provider}>{provider}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span>Model</span>
                      <select
                        className="select chat-model-select"
                        aria-label="Model name"
                        value={chatSelection?.model ?? ""}
                        onChange={(e) => chatSelection && void setModel({ ...chatSelection, model: e.target.value })}
                        disabled={!chatSelection?.provider}
                      >
                        <option value="" disabled>Model name</option>
                        {providerModels(chatSelection?.backend ?? "cloudflare", chatSelection?.provider ?? "").map((model) => (
                          <option key={model.id} value={model.id}>{model.id}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="row chat-header-actions">
          {chat && !isCompact && (
            <>
              <button className="btn btn-danger btn-sm" onClick={deleteChat}>
                Delete
              </button>
              <button className="btn btn-sm" onClick={() => { setCanvasOpen(true); setCanvasRequested(true); setAnswerMode(false); }}>
                Canvas
              </button>
              {debugCapture.isEnabled() && (
                <button
                  className="btn btn-sm btn-ghost chat-capture-btn"
                  onClick={captureEvent}
                  title="Download frontend + backend diagnostics for this chat"
                >
                  Capture Event
                </button>
              )}
            </>
          )}
          {chat && isCompact && (
            // Same actions, one tap away: a phone header has room for the
            // model chip or the button row, not both.
            <div className="chat-menu">
              <button
                className="btn btn-sm btn-ghost chat-menu-trigger"
                type="button"
                aria-label="Chat actions"
                aria-expanded={chatMenuOpen}
                onClick={() => setChatMenuOpen((o) => !o)}
              >
                ⋯
              </button>
              {chatMenuOpen && (
                <>
                  <div className="chat-menu-backdrop" onClick={() => setChatMenuOpen(false)} />
                  <div className="chat-menu-sheet" role="menu">
                    <button
                      className="chat-menu-item"
                      role="menuitem"
                      onClick={() => {
                        setChatMenuOpen(false);
                        setCanvasOpen(true);
                        setCanvasRequested(true);
                        setAnswerMode(false);
                      }}
                    >
                      🎨 Canvas
                    </button>
                    {debugCapture.isEnabled() && (
                      <button
                        className="chat-menu-item"
                        role="menuitem"
                        onClick={() => {
                          setChatMenuOpen(false);
                          void captureEvent();
                        }}
                      >
                        🐞 Capture Event
                      </button>
                    )}
                    <button
                      className="chat-menu-item chat-menu-item-danger"
                      role="menuitem"
                      onClick={() => {
                        setChatMenuOpen(false);
                        void deleteChat();
                      }}
                    >
                      🗑 Delete chat
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </header>

      <div
        className="chat-scroll"
        ref={scrollRef}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
      >
        {!chat && (
          <div className="empty-state">
            <h3>Welcome to chatter</h3>
            <p>Create a new chat from the sidebar to begin.</p>
          </div>
        )}
        {chat && messages.length === 0 && !streaming && (
          <div className="empty-state">
            <h3>Start the conversation</h3>
            <p>Type a message below. Drop images to analyze with vision.</p>
          </div>
        )}
        {chat && renderPublications(-1)}
        {messages.map((m, i) => (
          <Fragment key={m.id}>
            <MessageBubble
              message={m}
              toolResultsById={toolResultsById}
              chatId={chat?.id}
              // The receipt belongs to the turn that just ended, so it rides
              // in the action row of the final message rather than claiming a
              // bordered block of its own below the thread.
              receipt={!streaming && i === messages.length - 1 ? turnReceipt : null}
            />
            {renderPublications(i)}
          </Fragment>
        ))}
        {pendingTools.map((t) => (
          <ToolCallPanel
            key={t.id}
            name={t.name}
            arguments={t.arguments}
            id={t.id}
            result={t.result}
          />
        ))}
        {pendingConfirmation && (
          <ConfirmCard
            name={pendingConfirmation.name}
            source={pendingConfirmation.source}
            arguments={pendingConfirmation.arguments}
            disabled={confirmBusy}
            onApprove={() => respondToConfirmation(true)}
            onApproveAll={() => respondToConfirmation(true, true)}
            onDeny={() => respondToConfirmation(false)}
          />
        )}
        {streaming && !(!answerMode && chatSearchStage) && (
          <div className="turn-status" role="status" aria-live="polite">
            <span className="turn-status-dot" />
            <span>{pendingTools.length > 0 ? `Working · ${pendingTools[pendingTools.length - 1].name}` : "Working · preparing a response"}</span>
          </div>
        )}
        {streaming && !answerMode && chatSearchStage && (
          <StagePill stage="searching" providers={chatSearchStage.providers} execution={chatSearchStage.execution} />
        )}
        {streaming && answerMode && answerStage && !liveSources && (
          <StagePill stage={answerStage} providers={answerStageProviders} execution={answerStageExecution} />
        )}
        {/* Sources render before the first token (§0.6's perceived-speed
            trick) — while streamingText is still empty this is the only
            place they're visible; once text starts, the copy inside the
            msg-body-grid below takes over. */}
        {streaming && answerMode && !streamingText && liveSources && liveSources.length > 0 && (
          <SourceRail sources={liveSources} activeN={liveActiveN} providerErrors={liveProviderErrors} />
        )}
        {streaming && streamingText && (
          <div className="msg msg-assistant msg-research fade-in">
            <div className="msg-avatar">AI</div>
            <div className="msg-content">
              <div className={liveSources && liveSources.length > 0 ? "msg-body-grid" : undefined}>
                <div className="msg-answer-col">
                  {liveSources && liveSources.length > 0 && <PlacesPanel sources={liveSources} />}
                  <Markdown
                    content={streamingText}
                    onCitationSelect={(n) => {
                      setLiveActiveN(n);
                      setTimeout(() => {
                        document.getElementById(`source-${n}`)?.scrollIntoView({ behavior: "smooth", block: "nearest" });
                      }, 0);
                    }}
                  />
                  <span className="msg-cursor" />
                  {liveReport?.unverified && <UnverifiedBadge />}
                </div>
                {liveSources && liveSources.length > 0 && (
                  <div className="msg-source-col">
                    <SourceRail sources={liveSources} activeN={liveActiveN} providerErrors={liveProviderErrors} />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {/* Task 9.9 — single instance, not gated on `streaming`: this is
            the only React component instance rendering related-question
            chips, so it survives the streaming->persisted transition
            without an unmount/remount (which would silently drop a click
            that fired in between). */}
        {liveRelated && liveRelated.length > 0 && (
          <RelatedQuestions questions={liveRelated} onAsk={(q) => sendAnswer(q)} />
        )}
        {streaming && !streamingText && !(answerMode && answerStage) && !chatSearchStage && pendingTools.length === 0 && (
          <div className="msg msg-assistant">
            <div className="msg-avatar">AI</div>
            <div className="msg-content">
              <span className="spinner" /> <span className="muted">thinking…</span>
            </div>
          </div>
        )}
      </div>

      {loadError && <div className="chat-error">{loadError}</div>}
      {streamError && <div className="chat-error">{streamError}</div>}
      {truncated && !streaming && (
        <div className="chat-truncated-notice">Response stopped early.</div>
      )}

      {chat && (
        <div className={`chat-composer ${dragOver ? "drag" : ""}`}>
          {pendingFiles.length > 0 && (
            <div className="composer-images">
              {pendingFiles.map((f, i) => (
                <div key={`${f.name}-${i}`} className="composer-image">
                  {f.type.startsWith("image/") ? (
                    <img src={URL.createObjectURL(f)} alt={f.name} />
                  ) : (
                    <span className="composer-file" title={f.name}>{fileKindLabel(f)}</span>
                  )}
                  <span className="composer-file-name" title={f.name}>{f.name}</span>
                  <button
                    className="composer-image-remove"
                    onClick={() =>
                      setPendingFiles((p) => p.filter((_, j) => j !== i))
                    }
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="composer-input-wrap">
            <SlashCommandAutocomplete
              input={input}
              onSelect={(cmd: PluginCommand) => setInput(cmd.prompt)}
            />
            <textarea
              ref={composerInputRef}
              rows={1}
              className="textarea composer-input"
              placeholder={
                answerMode
                  ? isPhone
                    ? "Ask anything — with sources"
                    : "Ask a question — searches the web and cites sources…"
                  : isPhone
                    ? "Message"
                    : "Send a message…  (drop images for vision, / for plugin commands)"
              }
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                // On a touch keyboard Return is the newline key, not a send
                // key — sending on Enter there makes multi-line messages
                // impossible. The send button is the only send affordance.
                if (e.key === "Enter" && !e.shiftKey && !isCompact) {
                  e.preventDefault();
                  answerMode ? sendAnswer() : send();
                }
              }}
              disabled={streaming}
            />
          </div>
          {/* On a phone/tablet the option toggles live in a sheet that opens
              from the composer's "+" — the same move ChatGPT and Claude make.
              Nothing is removed: every toggle is one tap away, and the
              composer at rest is a single thumb-height bar instead of a
              four-row block filling a third of the screen. */}
          <div
            className={
              "composer-toggles" +
              (isCompact ? " composer-sheet" : "") +
              (isCompact && !optionsOpen ? " composer-sheet-closed" : "")
            }
          >
            {canvasRequested && <span className="tag tag-muted">Canvas requested for next turn</span>}
            {!isCompact && (
              <label className="toggle toggle-answer-mode" title="Search the web and write a structured, cited research answer">
                <input
                  type="checkbox"
                  checked={answerMode}
                  onChange={(e) => setAnswerMode(e.target.checked)}
                />
                <span>Ask mode</span>
              </label>
            )}
              <label
                className="toggle toggle-web-search"
                title="Grounds this reply with live web search results. Independent of Tools — for a full sourced answer with citation cards, use Ask mode instead."
              >
                <input
                  type="checkbox"
                  checked={useWebSearch}
                  onChange={(e) => {
                    setUseWebSearch(e.target.checked);
                    saveWebSearch(e.target.checked);
                  }}
                  disabled={answerMode}
                />
                <span>🌐 Web Search</span>
              </label>
              <label className="toggle" title="Let the model call MCP/plugin tools during this turn. Unrelated to web search.">
                <input
                  type="checkbox"
                  checked={useTools}
                  onChange={(e) => setUseTools(e.target.checked)}
                  disabled={answerMode}
                />
                <span>Tools</span>
              </label>
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={useMemory}
                  onChange={(e) => setUseMemory(e.target.checked)}
                />
                <span>Memory</span>
              </label>
              {skills.length > 0 && (
                <div className="skills-popover" ref={skillsPopoverRef}>
                  <button
                    className="btn btn-sm btn-ghost"
                    type="button"
                    onClick={() => setSkillsOpen((o) => !o)}
                  >
                    Skills ({activeSkills.length})
                  </button>
                  {skillsOpen && (
                    <div className="skills-popover-menu">
                      {skills.map((s) => (
                        <label key={s.name} className="skill-option">
                          <input
                            type="checkbox"
                            checked={activeSkills.includes(s.name)}
                            onChange={() => toggleSkill(s.name)}
                          />
                          <div>
                            <div className="skill-option-name">{s.name}</div>
                            <div className="skill-option-desc dim">
                              {s.description}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {reasoningOptions.length > 0 && (
                <div className="skills-popover" ref={thinkingPopoverRef}>
                  <button
                    className="btn btn-sm btn-ghost"
                    type="button"
                    aria-expanded={thinkingOpen}
                    title={
                      toolForcedEffort
                        ? `Reasoning depth for ${modelKey}. This model requires reasoning_effort="${toolForcedEffort}" whenever tools are attached, so a tool-calling turn uses that instead.`
                        : `Reasoning depth for ${modelKey}`
                    }
                    onClick={() => setThinkingOpen((o) => !o)}
                  >
                    Thinking ({thinkingEffort ?? "default"})
                  </button>
                  {thinkingOpen && (
                    <div className="skills-popover-menu">
                      <label className="skill-option">
                        <input
                          type="radio"
                          name="thinking-effort"
                          checked={thinkingEffort === null}
                          onChange={() => chooseThinking(null)}
                        />
                        <div>
                          <div className="skill-option-name">Default</div>
                          <div className="skill-option-desc dim">
                            Let the provider decide — the field is omitted.
                          </div>
                        </div>
                      </label>
                      {reasoningOptions.map((value) => (
                        <label key={value} className="skill-option">
                          <input
                            type="radio"
                            name="thinking-effort"
                            checked={thinkingEffort === value}
                            onChange={() => chooseThinking(value)}
                          />
                          <div>
                            <div className="skill-option-name">{value}</div>
                          </div>
                        </label>
                      ))}
                      {toolForcedEffort && (
                        <div className="skill-option-desc dim thinking-note">
                          Tool-calling turns on this model always send
                          “{toolForcedEffort}” — the provider rejects any other
                          value when tools are attached.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
          </div>
          <div className="composer-controls">
            <div className="composer-controls-left">
              {isCompact && (
                <>
                  <button
                    className="composer-icon-btn composer-more"
                    type="button"
                    aria-label={optionsOpen ? "Hide options" : "Show options"}
                    aria-expanded={optionsOpen}
                    onClick={() => setOptionsOpen((o) => !o)}
                  >
                    {optionsOpen ? "×" : "+"}
                  </button>
                  <label
                    className="toggle toggle-answer-mode"
                    title="Search the web and write a structured, cited research answer"
                  >
                    <input
                      type="checkbox"
                      checked={answerMode}
                      onChange={(e) => setAnswerMode(e.target.checked)}
                    />
                    <span>🔎 Ask mode</span>
                  </label>
                </>
              )}
            </div>
            <div className="row composer-actions">
              <input
                ref={fileInputRef}
                type="file"
                accept={ATTACHMENT_ACCEPT}
                multiple
                style={{ display: "none" }}
                onChange={(e) => onFileSelect(e.target.files)}
              />
              <button
                className={isCompact ? "composer-icon-btn" : "btn btn-sm"}
                onClick={() => fileInputRef.current?.click()}
                aria-label="Attach files"
                title="Attach files (videos up to 35 MB)"
              >
                📎
              </button>
              {streaming ? (
                <button
                  className={isCompact ? "composer-icon-btn composer-stop" : "btn btn-danger btn-sm"}
                  onClick={stop}
                  aria-label="Stop"
                >
                  {isCompact ? "■" : "Stop"}
                </button>
              ) : (
                <button
                  className={isCompact ? "composer-icon-btn composer-send" : "btn btn-primary btn-sm"}
                  onClick={() => (answerMode ? sendAnswer() : send())}
                  disabled={!input.trim()}
                  aria-label={answerMode ? "Ask" : "Send"}
                >
                  {isCompact ? "↑" : answerMode ? "Ask" : "Send"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {chat && canvasOpen && <CanvasPane key={`${chat.id}:${canvasRevisionTick}`} chatId={chat.id} onClose={() => setCanvasOpen(false)} />}
    </div>
  );
}

function MessageBubble({
  message,
  toolResultsById,
  chatId,
  receipt,
}: {
  message: Message;
  toolResultsById: Record<string, string>;
  chatId?: string;
  receipt?: { status: "complete" | "stopped" | "failed"; summary: string } | null;
}) {
  const isUser = message.role === "user";
  const isTool = message.role === "tool";
  const [activeN, setActiveN] = useState<number | null>(null);
  const toast = useToast();
  const saveAsMemory = () => saveMessageAsMemory(message.content, chatId, toast);
  // Task 9.9/5.11 wiring — `message.sources`/citation chips have existed
  // since Task 5.7/5.11 but were never actually rendered in ChatView
  // (SourceRail + Markdown's onCitationSelect were built and tested in
  // isolation, never wired here). Clicking a "[n]" chip scrolls to and
  // highlights the matching source card below the answer.
  const onCitationSelect = (n: number) => {
    setActiveN(n);
    // Deferred a tick: activeN flows into SourceRail as a prop, which
    // auto-expands the panel if it's collapsed — the target card only
    // exists in a scrollable position once that re-render has happened.
    setTimeout(() => {
      document.getElementById(`source-${n}`)?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 0);
  };

  if (isTool) {
    // F8-4 fix: a "tool" role message is now folded into the preceding
    // assistant message's ToolCallPanel (below, via toolResultsById) when
    // its tool_call_id matches one of that assistant turn's tool_calls —
    // rendered here only as a defensive fallback for an orphaned result
    // with no matching call in this list.
    if (message.tool_call_id && toolResultsById[message.tool_call_id] !== undefined) {
      return null;
    }
    return (
      <div className="msg msg-tool">
        <ToolCallPanel
          name={message.tool_call_id || "tool"}
          arguments={{}}
          result={message.content}
        />
      </div>
    );
  }

  // Assistant message with embedded tool_calls
  return (
    <div className={`msg ${isUser ? "msg-user" : "msg-assistant"} ${!isUser && message.sources?.length ? "msg-research" : ""} fade-in`}>
      <div className="msg-avatar">{isUser ? "You" : "AI"}</div>
      <div className="msg-content">
        {message.images && message.images.length > 0 && (
          <div className="msg-images">
            {message.images.map((src, i) => (
              isImageAttachment(src) ? (
                <img
                  key={i}
                  // Optimistic local attachments are blob: URLs. They are
                  // already browser-readable and must never be sent through
                  // the authenticated generated-image endpoint.
                  src={src.startsWith("data:") || src.startsWith("blob:") ? src : imageUrl(src)}
                  alt="attachment"
                />
              ) : (
                <span key={i} className="message-file" title={attachmentMime(src)}>
                  {attachmentMime(src).split("/").pop()?.toUpperCase() || "FILE"}
                </span>
              )
            ))}
          </div>
        )}
        {isUser ? (
          <>
            <div className="msg-text">{message.content}</div>
            <MessageActions
              copyLabel="Copy prompt"
              onCopy={() => copyMessageText(message.content, "Prompt", toast)}
              onSaveMemory={saveAsMemory}
            />
          </>
        ) : (
          <div className={message.sources && message.sources.length > 0 ? "msg-body-grid" : undefined}>
            <div className="msg-answer-col">
              {message.sources && message.sources.length > 0 && <PlacesPanel sources={message.sources} />}
              <Markdown content={message.content} onCitationSelect={onCitationSelect} />
              {message.citation_report?.unverified && <UnverifiedBadge />}
              {message.truncated && (
                <div className="msg-truncated-badge" title="This response was stopped before it finished.">
                  stopped early
                </div>
              )}
              {/* Tool panels belong to the message body: they render ABOVE the
                  action row so Copy/Download/Save-as-memory always sit at the
                  bottom of the whole message — with an empty content string the
                  actions row used to land level with the avatar and push the
                  panels below it. */}
              {message.tool_calls && message.tool_calls.length > 0 && (
                <div className="msg-toolcalls">
                  {message.tool_calls.map((tc, i) => (
                    <ToolCallPanel
                      key={i}
                      name={tc.function.name}
                      arguments={tc.function.arguments}
                      id={tc.id}
                      result={tc.id ? toolResultsById[tc.id] : undefined}
                    />
                  ))}
                </div>
              )}
              <MessageActions
                copyLabel="Copy response"
                onCopy={() => copyMessageText(message.content, "Response", toast)}
                onDownload={() => downloadResponse(message.content, toast)}
                onSaveMemory={saveAsMemory}
                receipt={receipt}
              />
            </div>
            {message.sources && message.sources.length > 0 && (
              <div className="msg-source-col">
                <SourceRail sources={message.sources} activeN={activeN} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** Copy: two offset sheets. Stroke-only so it inherits `currentColor`. */
function CopyIcon() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
      <rect x="5.5" y="5.5" width="8" height="8" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.3" />
      <path d="M10.5 3.5v-1a1 1 0 0 0-1-1h-7a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h1" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

/** Download: down arrow with a line under it. */
function DownloadIcon() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
      <path d="M8 2v8m0 0 3-3m-3 3-3-3" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M2.5 13.5h11" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Icon-only message actions. The labels moved to `title`/`aria-label`: the
 * three text buttons were a full extra line of chrome under every message,
 * and the turn receipt was a fourth. The receipt now rides in this same
 * row (borderless) so a completed turn costs no vertical space at all.
 */
function MessageActions({
  copyLabel,
  onCopy,
  onDownload,
  onSaveMemory,
  receipt,
}: {
  copyLabel: string;
  onCopy: () => void;
  onDownload?: () => void;
  onSaveMemory?: () => void;
  receipt?: { status: "complete" | "stopped" | "failed"; summary: string } | null;
}) {
  return (
    <div className="msg-actions">
      <button className="msg-action" type="button" onClick={onCopy} title={copyLabel} aria-label={copyLabel}>
        <CopyIcon />
      </button>
      {onDownload && (
        <button className="msg-action" type="button" onClick={onDownload} title="Download" aria-label="Download">
          <DownloadIcon />
        </button>
      )}
      {onSaveMemory && (
        <button
          className="msg-action"
          type="button"
          onClick={onSaveMemory}
          title="Save as memory"
          aria-label="Save as memory"
        >
          <span className="msg-action-emoji">💭</span>
        </button>
      )}
      {receipt && (
        <span className={`turn-receipt-inline turn-receipt-${receipt.status}`} role="status">
          <span className="turn-receipt-mark">
            {receipt.status === "complete" ? "✓" : receipt.status === "stopped" ? "⏹" : "!"}
          </span>
          {receipt.summary}
        </span>
      )}
    </div>
  );
}

async function saveMessageAsMemory(
  content: string,
  chatId: string | undefined,
  toast: ReturnType<typeof useToast>,
) {
  if (!content.trim()) return;
  try {
    await memoryApi.add({ content, source_chat_id: chatId });
    toast.success("Saved to memory");
  } catch (error) {
    toast.error((error as Error).message || "Could not save memory.");
  }
}

async function copyMessageText(
  content: string,
  label: string,
  toast: ReturnType<typeof useToast>,
) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(content);
    } else {
      const textarea = document.createElement("textarea");
      textarea.value = content;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      const copied = document.execCommand("copy");
      textarea.remove();
      if (!copied) throw new Error("Copy is not available in this browser.");
    }
    toast.success(`${label} copied`);
  } catch (error) {
    toast.error((error as Error).message || `Could not copy ${label.toLowerCase()}.`);
  }
}

function downloadResponse(content: string, toast: ReturnType<typeof useToast>) {
  try {
    const blob = new Blob([content], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const stamp = new Date().toISOString().replace(/[:.]/g, "-");
    link.href = url;
    link.download = `response-${stamp}.md`;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    toast.success("Response downloaded");
  } catch (error) {
    toast.error((error as Error).message || "Could not download response.");
  }
}

function fileKindLabel(file: File): string {
  const extension = file.name.includes(".") ? file.name.split(".").pop() : "file";
  return (extension || "file").slice(0, 8).toUpperCase();
}

function attachmentMime(src: string): string {
  const match = src.match(/^data:([^;,]+)/);
  return match?.[1] || "file";
}

function isImageAttachment(src: string): boolean {
  return attachmentMime(src).startsWith("image/") || src.startsWith("blob:");
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}
